import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TerminateDialog, ReactivateDialog } from "@/components/people/TerminateDialog";

const TABS = [["active", "Active People"], ["terminated", "Terminated People"], ["all", "All People"]];
const label = (c) => c.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

/** Admin Center → People: lifecycle view of the same CRM Person records. Terminate/Reactivate = Group Admin or higher. */
export default function AdminPeople() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "active";
  const [q, setQ] = useState(sp.get("q") || "");
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/people", { params: { tab, q } }).then((r) => setD(r.data)).catch((e) => { toast.error(apiError(e)); setD({ items: [] }); });
  }, [tab, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const done = (s) => { setDlg(null); toast.message(s); load(); };
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="admin-people">
      <h1 className="text-2xl font-semibold text-slate-900">People</h1>
      <p className="text-sm text-slate-500 mt-1">Person lifecycle for your Groups. <b>Terminate Person</b> changes business status and keeps all history. Login access is a separate choice.</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setSp({ tab: k, q })} data-testid={`ap-tab-${k}`}
          className={`px-3 py-1.5 text-sm rounded-md ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="h-9 max-w-xs ml-auto" data-testid="ap-search" />
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ap-table">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>
            <th className="p-2">Name</th><th className="p-2">Classification</th><th className="p-2">Group</th><th className="p-2">Agency</th>
            <th className="p-2">Terminated</th><th className="p-2">By</th><th className="p-2">Reason</th><th className="p-2">Login</th><th className="p-2">Last activity</th><th className="p-2" /></tr></thead>
          <tbody>
            {(d?.items || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`ap-row-${r.id}`}>
                <td className="p-2"><Link to={`/people/detail/${r.id}`} className="font-medium text-slate-900 hover:underline">{r.name}</Link>{r.status === "terminated" && <span className="ml-1 text-[10px] rounded bg-slate-800 text-white px-1">TERMINATED</span>}</td>
                <td className="p-2 text-xs">{r.classifications.map(label).join(", ") || "—"}</td>
                <td className="p-2 text-xs">{r.group || "—"}</td><td className="p-2 text-xs">{r.agency || "—"}</td>
                <td className="p-2 text-xs">{r.termination_date || "—"}</td><td className="p-2 text-xs">{r.terminated_by || "—"}</td><td className="p-2 text-xs">{r.reason || "—"}</td>
                <td className="p-2 text-xs capitalize">{r.login}</td><td className="p-2 text-xs">{r.last_activity || "—"}</td>
                <td className="p-2 text-right">{r.can_manage && (r.status === "terminated"
                  ? <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "reactivate", id: r.id })} data-testid={`ap-reactivate-${r.id}`}>Reactivate Person</Button>
                  : <Button size="sm" variant="outline" className="text-rose-700" onClick={() => setDlg({ kind: "terminate", id: r.id })} data-testid={`ap-terminate-${r.id}`}>Terminate Person</Button>)}</td>
              </tr>))}
            {d && !d.items.length && <tr><td colSpan={10} className="p-4 text-slate-500">No people in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      {dlg?.kind === "terminate" && <TerminateDialog personId={dlg.id} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === "reactivate" && <ReactivateDialog personId={dlg.id} onClose={() => setDlg(null)} onDone={done} />}
    </div>);
}
